import { useState } from "react";
import { PAKISTAN_PROVINCES } from "@aurelia/types";
import type { Address } from "@aurelia/types";
import { Button, Input, toast } from "@aurelia/ui";
import { useUpsertAddress } from "../../features/addresses/queries";

export function AddressForm({ initial, onDone }: { initial?: Address; onDone: () => void }) {
  const [label, setLabel] = useState(initial?.label ?? "");
  const [fullName, setFullName] = useState(initial?.fullName ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [line1, setLine1] = useState(initial?.line1 ?? "");
  const [line2, setLine2] = useState(initial?.line2 ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [province, setProvince] = useState(initial?.province ?? "");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [country, setCountry] = useState(initial?.country ?? "Pakistan");
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const upsert = useUpsertAddress();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    upsert.mutate(
      {
        id: initial?.id,
        label: label.trim() || undefined,
        fullName: fullName.trim(),
        phone: phone.trim(),
        line1: line1.trim(),
        line2: line2.trim() || undefined,
        city: city.trim(),
        province,
        postalCode: postalCode.trim(),
        country: country.trim(),
        isDefault
      },
      {
        onSuccess: () => {
          toast(initial ? "Address updated" : "Address saved", "success");
          onDone();
        },
        onError: (err) => toast((err as Error).message, "danger")
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 rounded-2xl border border-border bg-surface p-6">
      <Input
        label="Label (optional)"
        placeholder="e.g. Home, Office"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Full name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Input
          label="Phone"
          type="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      <Input label="Address line" required value={line1} onChange={(e) => setLine1(e.target.value)} />
      <Input
        label="Apartment, suite, etc. (optional)"
        value={line2}
        onChange={(e) => setLine2(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input label="City" required value={city} onChange={(e) => setCity(e.target.value)} />
        <Input label="Postal code" required value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
      </div>
      <label className="flex flex-col gap-2 text-sm">
        <span className="font-medium text-ink-muted">Province</span>
        <select
          required
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className="h-12 rounded-xl border border-border bg-surface px-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
        >
          <option value="" disabled>
            Select province / region
          </option>
          {PAKISTAN_PROVINCES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </label>
      <Input label="Country" required value={country} onChange={(e) => setCountry(e.target.value)} />

      <label className="flex items-center gap-2 text-sm text-ink-muted">
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
          className="h-4 w-4 rounded border-border accent-accent"
        />
        Set as default address
      </label>

      <div className="flex gap-3 pt-1">
        <Button type="submit" disabled={upsert.isPending}>
          {upsert.isPending ? "Saving…" : initial ? "Save changes" : "Save address"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
